/**
 * DataCloudPage - Refactored Version
 *
 * Main orchestration component for the Data Cloud workflow.
 * Uses custom hooks for feature logic and section components for UI.
 *
 * Workflow Steps:
 * 1. File Upload (2 CSV files: predictors + target variable)
 * 2. Regression Parameters & Processing
 * 3. Chart Results Display
 * 4. Interpolation
 * 5. Export
 *
 * Reduced from ~1,457 lines to ~350 lines through modularization.
 */

import React, { useReducer, useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// Core services
import { useKeepAlive } from '@/core/services/keepalive';
import DataCloudChunkedUploader from '@/features/data-cloud/services/DataCloudChunkedUploader';

// Feature hooks
import { useDataCloudFile } from '@/features/data-cloud/hooks/useDataCloudFile';
import { useDataCloudRegression } from '@/features/data-cloud/hooks/useDataCloudRegression';
import { useDataCloudInterpolation } from '@/features/data-cloud/hooks/useDataCloudInterpolation';
import { useDataCloudExport } from '@/features/data-cloud/hooks/useDataCloudExport';

// Feature components
import FileUploadSection from '@/features/data-cloud/components/FileUploadSection';
import RegressionParametersSection from '@/features/data-cloud/components/RegressionParametersSection';
import RegressionResultsSection from '@/features/data-cloud/components/RegressionResultsSection';
import InterpolationSection from '@/features/data-cloud/components/InterpolationSection';
import DataExportSection from '@/features/data-cloud/components/DataExportSection';

// Feature types and reducer
import {
  WORKFLOW_STEPS,
  INITIAL_STEP,
} from '@/features/data-cloud/types/dataCloud.types';
import { uiReducer, initialUIState } from '@/features/data-cloud/reducers/uiReducer';
import { mapCloudBackendError } from '@/features/data-cloud/utils/cloudBackendErrorMapper';

// Shared components
import InfoTooltip from '@/shared/components/forms/InfoTooltip';
import { usePublishChatContext } from '@/features/chatbot/usePublishChatContext';
import type { ChatField } from '@/features/chatbot/types';
import WorkflowStepper from '@/shared/components/layout/WorkflowStepper';
import NetworkStatusIndicator from '@/shared/components/feedback/NetworkStatusIndicator';
import SuccessMessage from '@/shared/components/feedback/SuccessMessage';
import ErrorMessage from '@/shared/components/feedback/ErrorMessage';
import ProgressTracker from '@/shared/components/feedback/ProgressTracker';
import FileNameModal from '@/shared/components/modals/FileNameModal';
import ConfirmDialog from '@/shared/components/modals/ConfirmDialog';
import { UpgradeModal } from '@/features/subscription/components';

const DataCloudPage: React.FC = () => {
  const { t } = useTranslation('dataCloud');

  // ═══════════════════════════════════════════════════════════════════
  // UI State Management (via reducer)
  // ═══════════════════════════════════════════════════════════════════
  const [uiState, dispatch] = useReducer(uiReducer, initialUIState);

  // Keep connection alive during long operations
  const isProcessingActive = uiState.isLoading || uiState.isInterpolating;
  useKeepAlive(null, isProcessingActive);

  // ═══════════════════════════════════════════════════════════════════
  // Upgrade Modal State
  // ═══════════════════════════════════════════════════════════════════
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState('');
  const [upgradeResourceType, setUpgradeResourceType] = useState<'storage' | 'processing'>('processing');

  // Confirm file removal mid-workflow (W10-FE8)
  const [pendingFileRemoval, setPendingFileRemoval] = useState<{ slot: 1 | 2 } | null>(null);

  // Upgrade modal handler
  const handleShowUpgradeModal = useCallback((message: string, type: 'processing' | 'storage') => {
    setUpgradeMessage(message);
    setUpgradeResourceType(type);
    setShowUpgradeModal(true);
  }, []);

  // ═══════════════════════════════════════════════════════════════════
  // Feature Hooks
  // ═══════════════════════════════════════════════════════════════════

  // File upload hook — dispatches SET_ERROR directly on validation failure,
  // matching the pattern used by regression and interpolation hooks.
  const fileHook = useDataCloudFile({ dispatch });

  // Regression hook (depends on file hook data)
  const regressionHook = useDataCloudRegression({
    file1: fileHook.file1,
    file2: fileHook.file2,
    dispatch,
    onShowUpgradeModal: handleShowUpgradeModal,
  });

  // Interpolation hook (depends on regression hook)
  const interpolationHook = useDataCloudInterpolation({
    file2: fileHook.file2,
    currentUploadId: regressionHook.currentUploadId,
    setCurrentUploadId: () => {}, // Handled internally
    decimalPrecision: regressionHook.decimalPrecision,
    dispatch,
    onShowUpgradeModal: handleShowUpgradeModal,
  });

  // Export hook (depends on all previous hooks)
  const exportHook = useDataCloudExport({
    file1: fileHook.file1,
    file2: fileHook.file2,
    xAxisLabel: fileHook.xAxisLabel,
    yAxisLabel: fileHook.yAxisLabel,
    chartData: regressionHook.chartData,
    interpolatedData: interpolationHook.interpolatedData,
    regressionType: regressionHook.regressionType,
    toleranceType: regressionHook.toleranceType,
    toleranceCnt: regressionHook.toleranceCnt,
    toleranceDep: regressionHook.toleranceDep,
    toleranceDepExtra: regressionHook.toleranceDepExtra,
    maxTimeSpan: interpolationHook.maxTimeSpan,
    saveDataName: uiState.saveDataName,
    dispatch,
  });

  // Publish current data-cloud settings to the chatbot assistant context
  const chatContext = useMemo(() => {
    const fields: ChatField[] = [];
    if (regressionHook.regressionType) fields.push({ label: 'Regressionstyp', value: String(regressionHook.regressionType) });
    if (regressionHook.toleranceType) fields.push({ label: 'Toleranztyp', value: String(regressionHook.toleranceType) });
    if (regressionHook.toleranceCnt) fields.push({ label: 'Toleranz', value: regressionHook.toleranceCnt });
    if (interpolationHook.maxTimeSpan) fields.push({ label: 'Max. Zeitspanne', value: interpolationHook.maxTimeSpan });
    return { fields };
  }, [regressionHook.regressionType, regressionHook.toleranceType, regressionHook.toleranceCnt, interpolationHook.maxTimeSpan]);
  usePublishChatContext(chatContext);

  // ═══════════════════════════════════════════════════════════════════
  // Workflow Step Management
  // ═══════════════════════════════════════════════════════════════════

  // Reset downstream state when a file is removed
  useEffect(() => {
    if (!fileHook.file1 || !fileHook.file2) {
      if (uiState.currentStep > INITIAL_STEP) {
        dispatch({ type: 'SET_STEP', payload: INITIAL_STEP });
        dispatch({ type: 'RESET_UI' });
        regressionHook.resetRegression();
        interpolationHook.resetInterpolation();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileHook.file1, fileHook.file2, uiState.currentStep]);

  // Auto-advance to step 2 when both files are uploaded
  useEffect(() => {
    if (fileHook.file1 && fileHook.file2 && uiState.currentStep === INITIAL_STEP) {
      dispatch({ type: 'SET_STEP', payload: 2 });
      dispatch({ type: 'MARK_STEP_COMPLETED', payload: 1 });
    }
  }, [fileHook.file1, fileHook.file2, uiState.currentStep]);

  // Auto-advance to step 3 when regression data is available
  useEffect(() => {
    if (regressionHook.chartData.length > 0 && uiState.currentStep === 2) {
      dispatch({ type: 'SET_STEP', payload: 3 });
      dispatch({ type: 'MARK_STEP_COMPLETED', payload: 2 });
    }
  }, [regressionHook.chartData.length, uiState.currentStep]);

  // Auto-advance to step 4 when chart is ready for interpolation
  useEffect(() => {
    if (regressionHook.chartData.length > 0 && uiState.currentStep === 3) {
      dispatch({ type: 'SET_STEP', payload: 4 });
      dispatch({ type: 'MARK_STEP_COMPLETED', payload: 3 });
    }
  }, [regressionHook.chartData.length, uiState.currentStep]);

  // Auto-advance to step 5 when interpolation is complete
  useEffect(() => {
    if (interpolationHook.interpolatedData.length > 0 && uiState.currentStep === 4) {
      dispatch({ type: 'SET_STEP', payload: 5 });
      dispatch({ type: 'MARK_STEP_COMPLETED', payload: 4 });
    }
  }, [interpolationHook.interpolatedData.length, uiState.currentStep]);

  // ═══════════════════════════════════════════════════════════════════
  // Handlers
  // ═══════════════════════════════════════════════════════════════════

  const handleRestart = useCallback(() => {
    // Cleanup Socket.IO connection
    if (regressionHook.currentUploadId) {
      DataCloudChunkedUploader.disconnectSocket(regressionHook.currentUploadId);
    }

    // Reset UI state via reducer
    dispatch({ type: 'RESET_UI' });

    // Reset all hooks
    fileHook.resetFiles();
    regressionHook.resetRegression();
    interpolationHook.resetInterpolation();

    // Reset file inputs by clearing their values
    const fileInputs = document.querySelectorAll(
      'input[type="file"]'
    ) as NodeListOf<HTMLInputElement>;
    fileInputs.forEach((input) => {
      input.value = '';
    });

    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [regressionHook.currentUploadId, fileHook, regressionHook, interpolationHook]);

  const handleOpenSaveModal = useCallback(() => {
    dispatch({ type: 'TOGGLE_MODAL', payload: true });
  }, []);

  const handleCloseSaveModal = useCallback(() => {
    dispatch({ type: 'TOGGLE_MODAL', payload: false });
  }, []);

  // ═══════════════════════════════════════════════════════════════════
  // Derived State
  // ═══════════════════════════════════════════════════════════════════
  const isProcessing = uiState.isLoading || uiState.isInterpolating;
  const hasData = !!(fileHook.file1 || fileHook.file2 || fileHook.rawData.length > 0);
  const hasInterpolatedData = interpolationHook.interpolatedData.length > 0;

  // Get current progress from active hook based on current operation
  const currentProgress = uiState.isInterpolating
    ? interpolationHook.progress
    : regressionHook.progress;

  // ═══════════════════════════════════════════════════════════════════
  // Render
  // ═══════════════════════════════════════════════════════════════════

  return (
    <div className="page-container" role="main" aria-label={t('a11y.title')}>
      {/* Messages Container */}
      <div className="messages-container" role="region" aria-label={t('a11y.statusMessages')} aria-live="polite">
        <div className="messages-wrapper">
          {(uiState.errorMessage || uiState.errorCode) && (
            <ErrorMessage
              message={(() => {
                const mapped = mapCloudBackendError({
                  code: uiState.errorCode ?? undefined,
                  message: uiState.errorMessage ?? undefined,
                  suggestion: uiState.errorSuggestion ?? undefined,
                });
                if (mapped.translationKey) {
                  return t(`dataCloud:${mapped.translationKey}`, {
                    defaultValue: mapped.fallbackMessage,
                  });
                }
                return mapped.fallbackMessage || uiState.errorMessage || '';
              })()}
              onClose={() => dispatch({ type: 'SET_ERROR', payload: null })}
              duration={uiState.errorPersistent ? 0 : 5000}
            />
          )}
          {uiState.successKey && (
            <SuccessMessage
              message={t(uiState.successKey, uiState.successParams ?? undefined)}
              onClose={() => dispatch({ type: 'SET_SUCCESS', payload: null })}
              duration={5000}
            />
          )}
        </div>
      </div>

      <NetworkStatusIndicator />

      <div className="main-container relative">
        {/* Workflow Stepper */}
        <nav aria-label={t('a11y.workflowProgress')}>
          <WorkflowStepper
            steps={WORKFLOW_STEPS}
            currentStep={uiState.currentStep}
            completedSteps={uiState.completedSteps}
            namespace="dataCloud"
          />
        </nav>

        <div
          className="card max-w-7xl mx-auto bg-card p-8 shadow-lg rounded-lg"
          role="form"
          aria-labelledby="data-cloud-section-title"
          aria-label={t('a11y.form')}
        >
          {/* Progress Overlay with ProgressTracker */}
          {isProcessing && currentProgress && (
            <div
              className="fixed inset-0 bg-background/90 z-50 flex items-center justify-center overflow-hidden"
              role="dialog"
              aria-labelledby="processing-title"
              aria-modal="true"
            >
              <div className="w-full max-w-lg p-4 sm:p-6 mx-4">
                <h3 id="processing-title" className="text-base sm:text-lg font-semibold text-center mb-3 sm:mb-4">
                  {t('process.processing')}
                </h3>
                <ProgressTracker
                  progress={currentProgress}
                  className="my-4 sm:my-8"
                />
              </div>
            </div>
          )}

          <div className="flex items-center mb-3 sm:mb-4 md:mb-6">
            <h2
              id="data-cloud-section-title"
              className="text-lg sm:text-xl md:text-2xl font-bold text-foreground"
            >
              {t('title')}
            </h2>
            <InfoTooltip text={t('tooltips.section')} width="w-96" />
          </div>

          <div className="space-y-3 sm:space-y-4">
            {/* Step 1: File Upload */}
            <FileUploadSection
              file1={fileHook.file1}
              file2={fileHook.file2}
              onFile1Upload={fileHook.handleFileUpload(1)}
              onFile2Upload={fileHook.handleFileUpload(2)}
              onRemoveFile1={() => {
                if (uiState.currentStep > INITIAL_STEP) {
                  setPendingFileRemoval({ slot: 1 });
                } else {
                  fileHook.removeFile1();
                }
              }}
              onRemoveFile2={() => {
                if (uiState.currentStep > INITIAL_STEP) {
                  setPendingFileRemoval({ slot: 2 });
                } else {
                  fileHook.removeFile2();
                }
              }}
            />

            {/* Step 2: Parameters & Chart Results */}
            {uiState.currentStep >= 2 && (
              <>
                <RegressionResultsSection
                  chartData={regressionHook.chartData}
                  rawData={fileHook.rawData}
                  xAxisLabel={fileHook.xAxisLabel}
                  yAxisLabel={fileHook.yAxisLabel}
                  pointSize={regressionHook.pointSize}
                  isLoading={uiState.isLoading}
                  onPointSizeChange={regressionHook.setPointSize}
                />

                <RegressionParametersSection
                  toleranceType={regressionHook.toleranceType}
                  regressionType={regressionHook.regressionType}
                  toleranceCnt={regressionHook.toleranceCnt}
                  toleranceDep={regressionHook.toleranceDep}
                  toleranceDepExtra={regressionHook.toleranceDepExtra}
                  onToleranceTypeChange={regressionHook.setToleranceType}
                  onRegressionTypeChange={regressionHook.setRegressionType}
                  onToleranceCntChange={regressionHook.setToleranceCnt}
                  onToleranceDepChange={regressionHook.setToleranceDep}
                  onToleranceDepExtraChange={regressionHook.setToleranceDepExtra}
                  onSubmit={regressionHook.handleSubmit}
                  isProcessing={uiState.isLoading}
                  canSubmit={!!(fileHook.file1 && fileHook.file2)}
                />
              </>
            )}

            {/* Step 4: Interpolation */}
            {regressionHook.chartData.length > 0 && (
              <InterpolationSection
                removedPoints={regressionHook.removedPoints || 0}
                maxTimeSpan={interpolationHook.maxTimeSpan}
                onMaxTimeSpanChange={interpolationHook.setMaxTimeSpan}
                decimalPrecision={regressionHook.decimalPrecision}
                onDecimalPrecisionChange={regressionHook.setDecimalPrecision}
                onInterpolate={interpolationHook.handleInterpolation}
                isInterpolating={uiState.isInterpolating}
                progress={interpolationHook.progress}
                interpolationInfo={interpolationHook.interpolationInfo}
                interpolatedData={interpolationHook.interpolatedData}
                yAxisLabel={fileHook.yAxisLabel}
                hasFile2={!!fileHook.file2}
              />
            )}

            {/* File Name Modal */}
            {uiState.isNameModalOpen && (
              <FileNameModal
                isOpen={uiState.isNameModalOpen}
                originalFileName={fileHook.file2?.name || t('chart.power')}
                onClose={handleCloseSaveModal}
                saveDataName={uiState.saveDataName}
                setSaveDataName={(name: string) => dispatch({ type: 'SET_SAVE_NAME', payload: name })}
                onSave={exportHook.saveData}
                setError={(error: string) =>
                  dispatch({
                    type: 'SET_ERROR',
                    payload: { code: undefined, suggestion: undefined, fallback: error },
                  })
                }
                isLoading={uiState.isLoading}
              />
            )}

            {/* Step 5: Export Actions */}
            <DataExportSection
              onSave={handleOpenSaveModal}
              onRestart={handleRestart}
              hasData={hasData}
              hasInterpolatedData={hasInterpolatedData}
            />
          </div>
        </div>
      </div>

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        resourceType={upgradeResourceType}
        message={upgradeMessage}
      />

      {/* Confirm file removal mid-workflow (W10-FE8) */}
      <ConfirmDialog
        isOpen={pendingFileRemoval !== null}
        title={t('a11y.confirmRemoveFile.title')}
        message={t('a11y.confirmRemoveFile.message')}
        confirmLabel={t('a11y.confirmRemoveFile.confirm')}
        cancelLabel={t('a11y.confirmRemoveFile.cancel')}
        destructive={true}
        onConfirm={() => {
          if (pendingFileRemoval?.slot === 1) {
            fileHook.removeFile1();
          } else if (pendingFileRemoval?.slot === 2) {
            fileHook.removeFile2();
          }
          setPendingFileRemoval(null);
        }}
        onCancel={() => setPendingFileRemoval(null)}
      />
    </div>
  );
};

export default DataCloudPage;
